import { resolvedApiBaseUrl, resolvedAuthApiBaseUrl } from '@/platform/lib/apiBase';
import { isCommunioProductSlugHost } from '@/platform/lib/authDomain';

const SCRIPT_ID = 'recaptcha-enterprise-sdk';

export const RECAPTCHA_LOGIN_ACTION = 'LOGIN';
/** Account Defender — profile / contact / linked-account changes. */
export const RECAPTCHA_ACCOUNT_UPDATE_ACTION = 'ACCOUNT_UPDATE';
/** Account Defender — contact email change on profile. */
export const RECAPTCHA_EMAIL_UPDATE_ACTION = 'EMAIL_UPDATE';

/** Persisted createAssessment name for Account Defender annotate on /login/complete. */
export const RECAPTCHA_LOGIN_ASSESSMENT_KEY = 'recaptchaLoginAssessmentId';

function siteKey(): string {
  const key = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY ?? '';
  if (!key) {
    throw new Error('NEXT_PUBLIC_RECAPTCHA_SITE_KEY is not configured');
  }
  return key;
}

/** True when a public site key is baked into the build (not E2E). */
export function isRecaptchaEnterpriseConfigured(): boolean {
  return (
    !process.env.NEXT_PUBLIC_E2E_MOCK_UID &&
    !!(process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY ?? '').trim()
  );
}

/**
 * Load Enterprise JS in the background for Account Defender horizontal telemetry.
 * Safe no-op when the site key is missing or this is an E2E build.
 * @see https://docs.cloud.google.com/recaptcha/docs/account-defender#provide-data
 */
export function ensureRecaptchaEnterpriseLoaded(): void {
  if (typeof window === 'undefined' || !isRecaptchaEnterpriseConfigured()) {
    return;
  }
  void loadRecaptchaEnterprise().catch(() => {
    /* telemetry preload — execute paths still try again */
  });
}

/** Load reCAPTCHA Enterprise (score) for login and account actions. */
export function loadRecaptchaEnterprise(): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('reCAPTCHA requires a browser'));
  }
  if (window.grecaptcha?.enterprise) {
    return Promise.resolve();
  }
  const existing = document.getElementById(SCRIPT_ID);
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('reCAPTCHA failed to load')), {
        once: true,
      });
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${encodeURIComponent(siteKey())}`;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('reCAPTCHA failed to load'));
    document.head.appendChild(script);
  });
}

/** Run invisible reCAPTCHA Enterprise (action LOGIN). */
export async function executeRecaptchaEnterprise(
  action = RECAPTCHA_LOGIN_ACTION,
): Promise<string> {
  await loadRecaptchaEnterprise();
  const grecaptcha = window.grecaptcha?.enterprise;
  if (!grecaptcha) {
    throw new Error('reCAPTCHA Enterprise is not available');
  }
  await new Promise<void>((resolve) => {
    grecaptcha.ready(() => resolve());
  });
  return grecaptcha.execute(siteKey(), { action });
}

export type VerifyRecaptchaOptions = {
  /** Login handle — sent as Account Defender userIds.email and default accountId. */
  email?: string;
  /** Stable account id when known (e.g. Firebase uid); defaults to normalized email. */
  accountId?: string;
  /** When true, store assessmentId for annotate after magic-link completion. */
  persistAssessment?: boolean;
};

function storeLoginAssessmentId(assessmentId: string | undefined): void {
  if (typeof window === 'undefined') return;
  if (assessmentId) {
    window.localStorage.setItem(RECAPTCHA_LOGIN_ASSESSMENT_KEY, assessmentId);
  } else {
    window.localStorage.removeItem(RECAPTCHA_LOGIN_ASSESSMENT_KEY);
  }
}

export function takeLoginAssessmentId(): string | null {
  if (typeof window === 'undefined') return null;
  const id = window.localStorage.getItem(RECAPTCHA_LOGIN_ASSESSMENT_KEY);
  window.localStorage.removeItem(RECAPTCHA_LOGIN_ASSESSMENT_KEY);
  return id;
}

/** Execute reCAPTCHA and verify the token with the API (createAssessment + userInfo). */
export async function verifyRecaptchaWithBackend(
  action = RECAPTCHA_LOGIN_ACTION,
  opts?: VerifyRecaptchaOptions,
): Promise<string | undefined> {
  // Product slugs: stay on-host (modal + auth API). Apex-only reCAPTCHA domains
  // must not block OTP — skip client assessment on slug hosts.
  if (typeof window !== 'undefined' && isCommunioProductSlugHost(window.location.hostname)) {
    return undefined;
  }

  const token = await executeRecaptchaEnterprise(action);
  const authBase = resolvedAuthApiBaseUrl();
  const sameOrigin = authBase === resolvedApiBaseUrl();
  const res = await fetch(`${authBase}/auth/verify-recaptcha`, {
    method: 'POST',
    credentials: sameOrigin ? 'include' : 'omit',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      token,
      action,
      ...(opts?.email ? { email: opts.email } : {}),
      ...(opts?.accountId ? { accountId: opts.accountId } : {}),
    }),
  });
  if (!res.ok) {
    throw new Error('reCAPTCHA verification failed. Please try again.');
  }
  let assessmentId: string | undefined;
  try {
    const body = (await res.json()) as { assessmentId?: string };
    assessmentId = body.assessmentId || undefined;
  } catch {
    assessmentId = undefined;
  }
  if (opts?.persistAssessment) {
    storeLoginAssessmentId(assessmentId);
  }
  return assessmentId;
}

export type AnnotateLoginRecaptchaOptions = {
  assessmentId?: string | null;
  loginSucceeded: boolean;
  email?: string;
  accountId?: string;
};

/** Annotate a prior LOGIN assessment after magic-link success or failure. */
export async function annotateLoginRecaptcha(opts: AnnotateLoginRecaptchaOptions): Promise<void> {
  const assessmentId = opts.assessmentId ?? takeLoginAssessmentId();
  if (!assessmentId || process.env.NEXT_PUBLIC_E2E_MOCK_UID) {
    return;
  }
  try {
    await fetch(`${resolvedApiBaseUrl()}/auth/annotate-recaptcha`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        assessmentId,
        loginSucceeded: opts.loginSucceeded,
        ...(opts.email ? { email: opts.email } : {}),
        ...(opts.accountId ? { accountId: opts.accountId } : {}),
      }),
    });
  } catch {
    /* best-effort — do not block sign-in UX */
  }
}

/** Frontend token + backend createAssessment (completes GCP key setup on login page load). */
export async function warmRecaptchaEnterprise(): Promise<void> {
  await verifyRecaptchaWithBackend(RECAPTCHA_LOGIN_ACTION);
}

export type ReportAccountActionOptions = {
  email?: string | null;
  accountId?: string | null;
  /** When true, also report EMAIL_UPDATE (contact email changed). */
  emailChanged?: boolean;
};

/**
 * Report account-modification actions to Account Defender (execute + createAssessment).
 * Best-effort: never blocks the user action if reCAPTCHA fails (no score gate server-side).
 */
export async function reportAccountModificationActions(
  opts: ReportAccountActionOptions = {},
): Promise<void> {
  if (process.env.NEXT_PUBLIC_E2E_MOCK_UID) {
    return;
  }
  const account = {
    ...(opts.email ? { email: opts.email } : {}),
    ...(opts.accountId ? { accountId: opts.accountId } : {}),
  };
  try {
    await verifyRecaptchaWithBackend(RECAPTCHA_ACCOUNT_UPDATE_ACTION, account);
    if (opts.emailChanged) {
      await verifyRecaptchaWithBackend(RECAPTCHA_EMAIL_UPDATE_ACTION, account);
    }
  } catch {
    /* report-only — proceed with the account change */
  }
}

declare global {
  interface Window {
    grecaptcha?: {
      enterprise?: {
        ready: (callback: () => void) => void;
        execute: (key: string, options: { action: string }) => Promise<string>;
      };
    };
  }
}
